import React, { useMemo } from 'react';

import { useGetMyDetails } from '../redux/api/users/usersApi';
import {
  PrimaryColor,
  ThemeMode,
  TrainingSetItemStatusListMode,
  UserConfigKey,
} from '../shared/constants';
import { configBool } from '../utils/common';
import { LoginUserStatus, useAuth } from './AuthContext';

type UserConfig = Record<string, string> | undefined;

export interface IUserConfigOptions {
  theme?: ThemeMode;
  primaryColor?: PrimaryColor;
  showSaveSetButton: boolean;
  showUploadSetButton: boolean;
  showExerciseUnits: boolean;
  onboardingEnabled: boolean;
  showTooltips: boolean;
  trainingSetItemStatusListMode: TrainingSetItemStatusListMode;
  defaultSetItemRestSec: number | null;
  notificationsSystemPushEnabled: boolean;
  notificationsSocialPushEnabled: boolean;
  notificationsMarketingPushEnabled: boolean;
  notificationsRemindersPushEnabled: boolean;
}

interface IUserConfigContextValue {
  config: UserConfig;
  options: IUserConfigOptions;
}

function parseDefaultRestSec(value: string | undefined): number | null {
  if (value === undefined || value === null || value === '') {
    return null;
  }
  const n = parseInt(value, 10);
  if (!Number.isFinite(n)) return null;
  return Math.min(300, Math.max(0, n));
}

function parseStatusListMode(
  value: string | undefined,
): TrainingSetItemStatusListMode {
  if (value === TrainingSetItemStatusListMode.primary) {
    return TrainingSetItemStatusListMode.primary;
  }
  return TrainingSetItemStatusListMode.all;
}

const UserConfigContext = React.createContext<
  IUserConfigContextValue | undefined
>(undefined);

interface IUserConfigProviderProps {
  children: React.ReactNode;
}

export function UserConfigProvider({ children }: IUserConfigProviderProps) {
  const { loginUserStatus } = useAuth();
  const { data: me } = useGetMyDetails({
    skip: loginUserStatus !== LoginUserStatus.loggedIn,
  });

  const config = me?.config;

  const value = useMemo<IUserConfigContextValue>(
    () => ({
      config,
      options: {
        theme: config?.[UserConfigKey.theme] as ThemeMode | undefined,
        primaryColor: config?.[UserConfigKey.primaryColor] as
          | PrimaryColor
          | undefined,
        showSaveSetButton: configBool(
          config?.[UserConfigKey.showSaveSetButton],
          false,
        ),
        showUploadSetButton: configBool(
          config?.[UserConfigKey.showUploadSetButton],
          false,
        ),
        showExerciseUnits: configBool(
          config?.[UserConfigKey.showExerciseUnits],
          true,
        ),
        onboardingEnabled: configBool(
          config?.[UserConfigKey.onboardingEnabled],
          true,
        ),
        showTooltips: configBool(config?.[UserConfigKey.showTooltips], true),
        trainingSetItemStatusListMode: parseStatusListMode(
          config?.[UserConfigKey.trainingSetItemStatusListMode],
        ),
        defaultSetItemRestSec: parseDefaultRestSec(
          config?.[UserConfigKey.defaultSetItemRestSec],
        ),
        notificationsSystemPushEnabled: configBool(
          config?.[UserConfigKey.notificationsSystemPushEnabled],
          false,
        ),
        notificationsSocialPushEnabled: configBool(
          config?.[UserConfigKey.notificationsSocialPushEnabled],
          false,
        ),
        notificationsMarketingPushEnabled: configBool(
          config?.[UserConfigKey.notificationsMarketingPushEnabled],
          false,
        ),
        notificationsRemindersPushEnabled: configBool(
          config?.[UserConfigKey.notificationsRemindersPushEnabled],
          false,
        ),
      },
    }),
    [config],
  );

  return (
    <UserConfigContext.Provider value={value}>
      {children}
    </UserConfigContext.Provider>
  );
}

export function useUserConfig(): IUserConfigContextValue {
  const context = React.useContext(UserConfigContext);
  if (context === undefined) {
    throw new Error('useUserConfig must be used within UserConfigProvider');
  }
  return context;
}
